/* eslint-disable camelcase */
// @noflow
import moment from 'moment';
import { isEmpty } from 'lodash';
import type { BaseEpisodeListing, Franchise } from '../franchise/types';

export default class BaseEventListing {
  _franchise: Franchise;
  _listing: BaseEpisodeListing;
  modelType: string;

  constructor(listing: BaseEpisodeListing, franchise: Franchise) {
    this._listing = listing;
    this._franchise = franchise;
    this.modelType = 'BaseEventListing';
  }

  get description() {
    return this._listing.description || '';
  }

  get echostarId() {
    if (this._listing.echostar_id) {
      return this._listing.echostar_id;
    }
    if (this._listing.es_id !== 0) {
      return this._listing.es_id;
    }
    if (this._listing.content_identification.echostar_id) {
      return this._listing.content_identification.echostar_id;
    }
    return undefined;
  }

  get contentId() {
    if (this._listing.content_id) {
      return this._listing.content_id;
    }
    if (this._listing.content_identification) {
      return this._listing.content_identification.content_id;
    }
    if (this._franchise.content_identification) {
      return this._franchise.content_identification.content_id;
    }
    return undefined;
  }

  get thumbnailUrl() {
    return this._listing.thumbnail_url || '';
  }

  get posterUrl() {
    return (
      this._listing?.poster_url ||
      this._listing?.thumbnail_url ||
      this._franchise.images?.poster_url ||
      this._franchise.images?.thumbnail_url
    );
  }

  get widePosterUrl() {
    return (
      this._listing?.wide_poster_url || this._franchise.images?.wide_poster_url || this.posterUrl
    );
  }

  get franchiseName() {
    return this._franchise.name;
  }

  get channelImage() {
    return this._listing.network_logo || this._franchise.networks.length > 0
      ? this._franchise.networks[0].logo
      : '';
  }

  get runtime() {
    return Math.floor(
      parseInt(
        this._listing.run_time || this._listing.duration || this._listing.total_seconds,
        10,
      ) / 60,
    );
  }

  get formattedRuntime() {
    const { runtime } = this;
    if (runtime == null || Number.isNaN(runtime) || runtime <= 0) return '';
    const hours = Math.floor(runtime / 60);
    const minutes = Math.floor(runtime % 60);

    if (runtime / 60 > 1 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (hours < 1) {
      return `${minutes}m`;
    }
    return `${hours}h`;
  }

  get remainingDuration() {
    return this._listing.remaining_duration;
  }

  get totalDuration() {
    return this._listing.total_duration;
  }

  get originalAirDate() {
    return this._listing.original_air_date;
  }

  get formattedAirDate() {
    return moment(this._listing.original_air_date)
      .utc()
      .format('MMMM D, YYYY');
  }

  get shortFormattedAirDate() {
    const date = this._listing.original_air_date || this._listing.rec_time;
    return !isEmpty(date)
      ? moment(date)
          .utc()
          .format('M/D/YY')
      : '';
  }

  get formattedAirYear() {
    if (this._listing.original_air_date == null || this._listing.original_air_date === '') {
      return '';
    }
    return moment(this._listing.original_air_date)
      .utc()
      .format('YYYY');
  }

  get franchiseSlug() {
    return this._listing.franchise_slug || this._franchise.slug;
  }

  get slug() {
    return this._listing.slug;
  }

  get videoId() {
    return this._listing.video_id || 0; // 0 will return generic custom_attributes object with
    // with required conviva tokens.
  }

  get episodeId() {
    return this._listing.episode_id;
  }

  get playerType() {
    return this._listing.player_type;
  }

  get purchases() {
    const { purchases } = this._franchise;
    if (purchases && purchases.length) {
      return purchases[0];
    }
    return null;
  }

  get purchasePlayerType() {
    if (this._listing.purchases && this._listing.purchases.length) {
      return this._listing.purchases[0].player_type;
    }
    if (this._franchise.purchases && this._franchise.purchases.length) {
      return this._franchise.purchases[0].player_type;
    }
    return undefined;
  }

  get seriesId() {
    if (this._listing.series_id) {
      return this._listing.series_id;
    }
    if (this._franchise.content_identification) {
      return this._franchise.content_identification.series_id;
    }
    return null;
  }

  get watchNow() {
    return false;
  }

  get type() {
    return this._franchise.kind;
  }

  json() {
    return this._listing;
  }
}
